
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { FileText, MessageSquare, Users, Activity } from "lucide-react";

interface UsageAnalyticsProps {
  organizationId: string;
}

interface AnalyticsData {
  totalDocuments: number;
  totalChats: number;
  activeMembers: number;
  documentsThisWeek: any[];
}

export function UsageAnalytics({ organizationId }: UsageAnalyticsProps) {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalDocuments: 0,
    totalChats: 0,
    activeMembers: 0,
    documentsThisWeek: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [organizationId]);

  const fetchAnalytics = async () => {
    try {
      // Get total documents
      const { count: documentCount } = await supabase
        .from("documents")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId);

      // Get total chats
      const { count: chatCount } = await supabase
        .from("chat_conversations")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId);

      // Get active members
      const { count: memberCount } = await supabase
        .from("organization_memberships")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", organizationId)
        .not("joined_at", "is", null);

      // Get documents created this week (last 7 days)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { data: weeklyDocs } = await supabase
        .from("documents")
        .select("created_at, type")
        .eq("organization_id", organizationId)
        .gte("created_at", weekAgo.toISOString());

      // Process weekly data for chart
      const dailyData = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        const dateStr = date.toISOString().split('T')[0];
        
        const docsForDay = weeklyDocs?.filter(doc => 
          doc.created_at.split('T')[0] === dateStr
        ).length || 0;

        return {
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          documents: docsForDay
        };
      });

      setAnalytics({
        totalDocuments: documentCount || 0,
        totalChats: chatCount || 0,
        activeMembers: memberCount || 0,
        documentsThisWeek: dailyData
      });

    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalDocuments}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Chats</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalChats}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.activeMembers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weekly Activity</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.documentsThisWeek.reduce((sum, day) => sum + day.documents, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Documents this week</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Document Creation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.documentsThisWeek}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="documents" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
